import React, { useMemo } from "react";
import {
  Box,
  Typography,
  IconButton,
  Checkbox,
  Grid,
  Fab,
  Chip,
  ButtonBase,
} from "@mui/material";
import {
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  AssignmentOutlined as TaskIcon,
  FileDownloadOutlined as ExportIcon,
} from "@mui/icons-material";
import { BRAND } from "../../components/auth/authTheme";
import MobileSelectField from "../../components/mobile/MobileSelectField";

const NAVY = BRAND.navy;
const ORANGE = BRAND.orangeHover;
const BG = "#F8FAFC";

const statusOptions = [
  "New",
  "Pending",
  "In Progress",
  "Ready for Testing",
  "Testing",
  "Changes Required",
  "Reopened",
  "Completed",
  "Blocked"
];
const priorityOptions = ["Low", "Medium", "High", "Critical"];

const statusColors = {
  New: "#2196F3",
  Pending: "#FF9800",
  "In Progress": "#9C27B0",
  "Ready for Testing": "#00BCD4",
  Testing: "#3F51B5",
  "Changes Required": "#FF5722",
  Reopened: "#FFC107",
  Completed: "#4CAF50",
  Blocked: "#F44336",
};

const priorityColors = {
  Low: "#8BC34A",
  Medium: "#FFC107",
  High: "#FF5722",
  Critical: "#F44336",
};

const TabPill = ({ label, isActive, onClick }) => (
  <ButtonBase
    onClick={onClick}
    sx={{
      flex: 1,
      py: 1,
      borderRadius: "10px",
      fontWeight: 600,
      fontSize: "0.8125rem",
      color: isActive ? ORANGE : BRAND.muted,
      bgcolor: isActive ? BRAND.orangeLight : "transparent",
      border: `1px solid ${isActive ? ORANGE : BRAND.border}`,
      transition: "all 0.15s",
    }}
  >
    {label}
  </ButtonBase>
);

const StatCard = ({ icon, label, value, iconBg, iconColor }) => (
  <Box
    sx={{
      bgcolor: "#fff",
      borderRadius: "12px",
      px: 0.75,
      py: 1,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      textAlign: "center",
      gap: 0.5,
      border: `1px solid ${BRAND.border}`,
      height: "100%",
      minWidth: 0,
    }}
  >
    <Box
      sx={{
        width: 30,
        height: 30,
        borderRadius: "8px",
        bgcolor: iconBg,
        color: iconColor,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {icon}
    </Box>
    <Typography sx={{ fontSize: "1rem", fontWeight: 700, color: NAVY, lineHeight: 1 }}>
      {value}
    </Typography>
    <Typography
      sx={{
        fontSize: "0.625rem",
        color: BRAND.muted,
        fontWeight: 500,
        lineHeight: 1.25,
        whiteSpace: "normal",
        wordBreak: "break-word",
        width: "100%",
      }}
    >
      {label}
    </Typography>
  </Box>
);

const formatDate = (value) => {
  if (!value) return "—";
  return new Date(value).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const getAssigneeName = (task) => {
  if (task.assigned_to?.name) return task.assigned_to.name;
  const first = task.assigned_to?.first_name || "";
  const last = task.assigned_to?.last_name || "";
  const full = `${first} ${last}`.trim();
  return full || "—";
};

const TaskCard = ({
  task,
  isSelected,
  onToggleSelect,
  onView,
  onEdit,
  onDelete,
  onStatusChange,
  onPriorityChange,
  canEdit,
  canDelete,
}) => (
  <Box
    sx={{
      bgcolor: "#fff",
      borderRadius: "12px",
      mb: 1.25,
      border: isSelected ? `2px solid ${ORANGE}` : `1px solid ${BRAND.border}`,
      boxShadow: "0 1px 6px rgba(26, 43, 74, 0.04)",
      overflow: "hidden",
    }}
  >
    <Box sx={{ px: 1.25, py: 0.875 }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "24px 1fr auto auto auto",
          alignItems: "center",
          columnGap: 0.35,
          mb: 0.35,
        }}
      >
        <Checkbox
          size="small"
          checked={isSelected}
          onChange={() => onToggleSelect(task)}
          sx={{
            p: 0,
            color: BRAND.muted,
            "&.Mui-checked": { color: ORANGE },
          }}
          inputProps={{ "aria-label": `Select ${task.title}` }}
        />
        <Typography
          sx={{
            fontWeight: 700,
            color: NAVY,
            fontSize: "0.875rem",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            minWidth: 0,
          }}
        >
          {task.title || "Untitled"}
        </Typography>
        <IconButton
          size="small"
          onClick={() => onView(task)}
          aria-label="View task"
          sx={{ p: 0, color: "#2196f3", width: 32, height: 32 }}
        >
          <VisibilityIcon sx={{ fontSize: "1rem" }} />
        </IconButton>
        {canEdit && (
          <IconButton
            size="small"
            onClick={() => onEdit(task)}
            aria-label="Edit task"
            sx={{ p: 0, color: ORANGE, width: 32, height: 32 }}
          >
            <EditIcon sx={{ fontSize: "1rem" }} />
          </IconButton>
        )}
        {canDelete && (
          <IconButton
            size="small"
            onClick={() => onDelete(task.id)}
            aria-label="Delete task"
            sx={{ p: 0, color: "#d32f2f", width: 32, height: 32 }}
          >
            <DeleteIcon sx={{ fontSize: "1rem" }} />
          </IconButton>
        )}
      </Box>

      <Box sx={{ pl: "calc(24px + 6px)" }}>
        <Typography sx={{ fontSize: "0.6875rem", color: BRAND.muted, mb: 0.5 }}>
          Assigned to: {getAssigneeName(task)}
        </Typography>
        <Typography sx={{ fontSize: "0.6875rem", color: BRAND.muted, mb: 0.75 }}>
          Created: {formatDate(task.created_at)}
        </Typography>

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mb: canEdit ? 0.75 : 0 }}>
          <Chip
            label={task.priority || "—"}
            size="small"
            sx={{
              height: 22,
              fontSize: "0.6875rem",
              fontWeight: 600,
              bgcolor: `${priorityColors[task.priority] || BRAND.muted}22`,
              color: priorityColors[task.priority] || BRAND.muted,
            }}
          />
          <Chip
            label={task.status || "—"}
            size="small"
            sx={{
              height: 22,
              fontSize: "0.6875rem",
              fontWeight: 600,
              bgcolor: `${statusColors[task.status] || BRAND.muted}22`,
              color: statusColors[task.status] || BRAND.muted,
            }}
          />
        </Box>

        {canEdit && (
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <MobileSelectField
                label="Priority"
                value={task.priority || ""}
                onChange={(val) => onPriorityChange(task.id, val)}
                options={priorityOptions.map((opt) => ({ label: opt, value: opt }))}
                placeholder="Priority"
              />
            </Grid>
            <Grid item xs={6}>
              <MobileSelectField
                label="Status"
                value={task.status || ""}
                onChange={(val) => onStatusChange(task.id, val)}
                options={statusOptions.map((opt) => ({ label: opt, value: opt }))}
                placeholder="Status"
              />
            </Grid>
          </Grid>
        )}
      </Box>
    </Box>
  </Box>
);

const TaskMobileView = ({
  tabValue,
  onTabChange,
  projects,
  selectedProjectId,
  onProjectChange,
  tasks,
  selectedRows,
  onToggleSelect,
  onView,
  onEdit,
  onDelete,
  onStatusChange,
  onPriorityChange,
  onAdd,
  onExport,
  canCreate,
  canEdit,
  canDelete,
  canExport,
  employeeMode = false,
}) => {
  const selectedIds = useMemo(
    () => new Set(selectedRows.map((row) => String(row.id))),
    [selectedRows]
  );

  const projectOptions = useMemo(
    () =>
      (projects || []).map((p) => ({
        label: p.title,
        value: p.id,
      })),
    [projects]
  );

  const stats = useMemo(() => {
    const byStatus = statusOptions.reduce((acc, status) => {
      acc[status] = tasks.filter((t) => t.status === status).length;
      return acc;
    }, {});
    return {
      total: tasks.length,
      inProgress: byStatus["In Progress"] || 0,
      completed: byStatus.Completed || 0,
    };
  }, [tasks]);

  const subtitle = employeeMode
    ? tabValue === "Task"
      ? "Your assigned tasks across projects"
      : "Issues assigned to you"
    : tabValue === "Task"
      ? "Track assigned tasks for the selected project"
      : "Log and resolve project issues";

  const emptyMessage = !selectedProjectId
    ? "Select a project first"
    : "No records found";

  const emptyHint = !selectedProjectId
    ? "Choose a project from the picker above."
    : canCreate
      ? "Tap + to create a task or issue."
      : "No tasks or issues for this project.";

  return (
    <Box sx={{ pb: 10, pt: 1, px: 2, bgcolor: BG, minHeight: "100vh" }}>
      <Typography sx={{ fontSize: "1.25rem", fontWeight: 800, color: NAVY, mb: 0.25 }}>
        {employeeMode ? "My Tasks" : "Tasks & Issues"}
      </Typography>
      <Typography sx={{ fontSize: "0.8125rem", color: BRAND.muted, mb: 1.5 }}>
        {subtitle}
      </Typography>

      <Box sx={{ mb: 1.5 }}>
        <MobileSelectField
          label="Project"
          value={selectedProjectId || ""}
          onChange={onProjectChange}
          options={projectOptions}
          placeholder="Select project"
          sheetTitle="Select Project"
        />
      </Box>

      <Box sx={{ display: "flex", gap: 1, mb: 1.5 }}>
        <TabPill
          label="Assigned Task"
          isActive={tabValue === "Task"}
          onClick={() => onTabChange("Task")}
        />
        <TabPill
          label="Issue Tracker"
          isActive={tabValue === "Issue"}
          onClick={() => onTabChange("Issue")}
        />
      </Box>

      {selectedProjectId && (
        <Grid container spacing={1} sx={{ mb: 1.5 }}>
          <Grid item xs={4} sx={{ minWidth: 0 }}>
            <StatCard
              icon={<TaskIcon sx={{ fontSize: "1rem" }} />}
              label="Total"
              value={stats.total}
              iconBg="rgba(33,150,243,0.15)"
              iconColor="#2196f3"
            />
          </Grid>
          <Grid item xs={4} sx={{ minWidth: 0 }}>
            <StatCard
              icon={<TaskIcon sx={{ fontSize: "1rem" }} />}
              label="In Progress"
              value={stats.inProgress}
              iconBg="rgba(156,39,176,0.15)"
              iconColor="#9C27B0"
            />
          </Grid>
          <Grid item xs={4} sx={{ minWidth: 0 }}>
            <StatCard
              icon={<TaskIcon sx={{ fontSize: "1rem" }} />}
              label="Completed"
              value={stats.completed}
              iconBg="rgba(76,175,80,0.15)"
              iconColor="#4caf50"
            />
          </Grid>
        </Grid>
      )}

      {canExport && selectedRows.length > 0 && (
        <Box
          sx={{
            mb: 1.5,
            p: 1.25,
            bgcolor: BRAND.orangeLight,
            borderRadius: "12px",
            border: `1px solid ${ORANGE}33`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Typography sx={{ fontSize: "0.8125rem", fontWeight: 600, color: NAVY }}>
            {selectedRows.length} selected
          </Typography>
          <Box
            component="button"
            type="button"
            onClick={onExport}
            sx={{
              border: "none",
              borderRadius: "10px",
              bgcolor: ORANGE,
              color: "#fff",
              fontWeight: 600,
              fontSize: "0.75rem",
              px: 1.5,
              py: 0.75,
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              cursor: "pointer",
            }}
          >
            <ExportIcon sx={{ fontSize: "0.95rem" }} />
            Export
          </Box>
        </Box>
      )}

      {selectedProjectId && tasks.length > 0 ? (
        tasks.map((task) => (
          <TaskCard
            key={task.id}
            task={task}
            isSelected={selectedIds.has(String(task.id))}
            onToggleSelect={onToggleSelect}
            onView={onView}
            onEdit={onEdit}
            onDelete={onDelete}
            onStatusChange={onStatusChange}
            onPriorityChange={onPriorityChange}
            canEdit={canEdit}
            canDelete={canDelete}
          />
        ))
      ) : (
        <Box
          sx={{
            textAlign: "center",
            py: 6,
            px: 2,
            bgcolor: "#fff",
            borderRadius: "16px",
            border: `1px solid ${BRAND.border}`,
          }}
        >
          <TaskIcon sx={{ fontSize: 48, color: "#CBD5E1", mb: 1 }} />
          <Typography sx={{ fontWeight: 600, color: NAVY, mb: 0.5 }}>
            {emptyMessage}
          </Typography>
          <Typography sx={{ fontSize: "0.875rem", color: BRAND.muted }}>
            {emptyHint}
          </Typography>
        </Box>
      )}

      {canCreate && (
        <Fab
          color="primary"
          aria-label="Add task or issue"
          onClick={onAdd}
          disabled={!selectedProjectId}
          size="medium"
          sx={{
            position: "fixed",
            bottom: 'calc(90px + env(safe-area-inset-bottom, 0px))',
            right: 20,
            bgcolor: ORANGE,
            "&:hover": { bgcolor: BRAND.orangeHover },
            "&.Mui-disabled": { bgcolor: "#CBD5E1", color: "#fff" },
            boxShadow: "0 4px 16px rgba(230, 90, 0, 0.35)",
            zIndex: 1002,
          }}
        >
          <AddIcon />
        </Fab>
      )}
    </Box>
  );
};

export default TaskMobileView;
